import React, { useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Item, Section } from "react-stately";
import { sessionStore } from "../../../../contexts/SessionContext";
import { fullName } from "../../../../utils/format";
import SearchAutocomplete from "../../../../components/SearchAutocomplete/SearchAutocomplete";
import { UserAvatar } from "../../../../components/Avatar/Avatar";

import "./StudentSearch.scss";

type Props = {
    onClick: (classroomId: string, studentId: string) => void;
    className?: string;
};

const StudentSearch = ({ onClick, className }: Props) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const intl = useIntl();
    const {
        session: { classrooms },
    } = useContext(sessionStore);

    const classroomsWithStudents = classrooms?.filter(
        (classroom) => classroom.students.length > 0
    );

    return (
        <SearchAutocomplete
            menuTrigger="focus"
            defaultItems={classroomsWithStudents}
            inputValue={searchValue}
            onInputChange={setSearchValue}
            onSelectionChange={(path) => {
                if (path) {
                    const { classroomId, studentId } = fromPath(path);
                    onClick(classroomId, studentId);
                    setSearchValue("");
                }
            }}
            label={intl.formatMessage(messages.searchStudent)}
            placeholder={intl.formatMessage(messages.searchStudent)}
            hideLabel
            className={className}
        >
            {(classroom) => (
                <Section
                    key={classroom.id}
                    title={classroom.name}
                    items={classroom.students}
                >
                    {(student) => (
                        <Item
                            key={toPath(classroom.id, student.id)}
                            textValue={fullName(student, {
                                capitalizeLastName: true,
                                startWithLastName: true,
                            })}
                        >
                            <UserAvatar user={student} />
                            {fullName(student, {
                                capitalizeLastName: true,
                                startWithLastName: true,
                            })}
                        </Item>
                    )}
                </Section>
            )}
        </SearchAutocomplete>
    );
};

export default StudentSearch;

const toPath = (classroomId: string, studentId: string) =>
    `${classroomId}/${studentId}`;

const fromPath = (path: React.Key) => {
    const [classroomId, studentId] = path.toString().split("/");
    return {
        classroomId,
        studentId,
    };
};

const messages = defineMessages({
    searchStudent: {
        id: "dashboard-common-searchStudent",
        defaultMessage: "Search students",
    },
});
